import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import { Col, Form, Row, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import debounce from 'lodash/debounce';

// Common Components Import
import ProductTableSales from '../../../Common/SalesMProduct';
import ChargesSection from "../../../Common/ChargesSection";
import { CHARGE_OPTIONS, UNIT_OPTIONS } from "../../../Common/constants";
import CommonDcFields from '../../DC/CommonDcFields';

import { BASE_URL } from '../../../services/Api';
import { authHeader } from '../../../services/auth-header';

function DCMDConvert() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [dcno, setdcno] = useState('');
    const [dcdate, setdcdate] = useState('');
    const [buyerorderno, setbuyerorderno] = useState('');
    const [buyerorderdate, setAmendmentdate] = useState('');
    const [destination, setdestination] = useState('');
    const [dispatchthrough, setdispatchthrough] = useState('');
    const [reference, setreference] = useState('');
    const [policyno, setpolicyno] = useState('');
    const [validupto, setvalidupto] = useState('');
    const [tallyreferenceno, settallyreferenceno] = useState('');
    const [termsofdelivery, settermsofdelivery] = useState('');
    const [status, setStatus] = useState('');
    const [invoicedate, setinvoicedate] = useState('');
    const [invoiceno, setinvoiceno] = useState('');
    const [enquiryValidationMsg, setEnquiryValidationMsg] = useState('');
    const [isEnquiryValid, setIsEnquiryValid] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);

    // Common fields data state
    const [commonFieldsData, setCommonFieldsData] = useState({
        customer: null,
        customerDetails: {
            contactperson: '',
            contactno: '',
            email: '',
            freight: '',
            packingandforwarding: '',
            paymentterm: ''
        },
        customerMultipleAddress: null,
        branch: null,
        warehouse: null,
        shipTo: null,
        billTo: null,
        remarks: '',
    });

    // Product and charges state
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productOptions, setProductOptions] = useState([]);
    const [productSearch, setProductSearch] = useState(encodeURIComponent(" "));
    const [rows, setRows] = useState([]);
    const [additionalCharges, setAdditionalCharges] = useState([
        {
            id: Date.now(),
            description: "",
            chargepercent: 0,
            amount: 0,
            gstPercent: 0,
            isPercentBased: true,
        },
    ]);

    // Validate sales invoice number
    const checkinvoiceno = async (number) => {
        if (!number) {
            setEnquiryValidationMsg('');
            setIsEnquiryValid(false);
            return;
        }

        if (!/^[A-Za-z0-9-]+$/.test(number)) {
            setEnquiryValidationMsg('❌ Only alphanumeric characters and hyphens are allowed');
            setIsEnquiryValid(false);
            return;
        }

        try {
            const response = await axios.get(`${BASE_URL}sales/check/${number}`, {
                headers: authHeader(),
            });

            if (response.data === true) {
                setEnquiryValidationMsg('❌ This sales invoice number already exists.');
                setIsEnquiryValid(false);
            } else if (response.data === false) {
                setEnquiryValidationMsg('✅ This sales invoice number is available.');
                setIsEnquiryValid(true);
            } else {
                setEnquiryValidationMsg('Unexpected response from server.');
                setIsEnquiryValid(false);
            }
        } catch (error) {
            console.error("Error checking sales invoice number:", error);
            setEnquiryValidationMsg('❌ Error validating sales invoice number.');
            setIsEnquiryValid(false);
        }
    };

    useEffect(() => {
        const debouncedCheck = debounce(() => {
            checkinvoiceno(invoiceno);
        }, 400);

        debouncedCheck();

        return () => debouncedCheck.cancel();
    }, [invoiceno]);

    // Fetch delivery challan data
    useEffect(() => {
        const fetchDeliveryChallan = async () => {
            try {
                const response = await axios.get(`${BASE_URL}deliveryChallan/findById/${id}`, {
                    headers: authHeader(),
                });
                const data = response.data;

                // Set basic fields with 2 decimal places
                setinvoiceno(data.invoiceno || '');
                setinvoicedate(data.invoicedate?.split('T')[0] || '');
                setdcno(data.deliverynoteno || '');
                setdcdate(data.deliverynotedate?.split('T')[0] || '');
                setbuyerorderno(data.buyerorderno || '');
                setAmendmentdate(data.buyerorderdate?.split('T')[0] || '');
                setdestination(data.destination || '');
                setdispatchthrough(data.dispatchthrough || '');
                setreference(data.reference || '');
                setpolicyno(data.policyno || '');
                setvalidupto(data.validupto?.split('T')[0] || '');
                settallyreferenceno(data.tallyreferenceno || '');
                settermsofdelivery(data.termsofdelivery || '');
                setStatus(data.status || '');

                // Set common fields
                setCommonFieldsData({
                    remarks: data.remarks,
                    customer: data.customer ? {
                        value: data.customer.id,
                        label: data.customer.companyname
                    } : null,
                    customerDetails: {
                        contactperson: data.contactperson || '',
                        contactno: data.contactno || '',
                        email: data.email || '',
                        freight: data.freight || '',
                        packingandforwarding: data.packingandforwarding || '',
                        paymentterm: data.paymentterm || ''
                    },
                    customerMultipleAddress: data.customeraddress ? {
                        value: data.customeraddress.id,
                        label: data.customeraddress.customeraddress
                    } : null,
                    branch: data.branch ? {
                        value: data.branch.id,
                        label: data.branch.branchname
                    } : null,
                    warehouse: data.warehouse ? {
                        value: data.warehouse.id,
                        label: data.warehouse.warehousename
                    } : null,
                    shipTo: data.shipto ? {
                        value: data.shipto.id,
                        label: `${data.shipto.customeraddress}, ${data.shipto.city?.cityname}`
                    } : null,
                    billTo: data.billto ? {
                        value: data.billto.id,
                        label: `${data.billto.customeraddress}, ${data.billto.city?.cityname}`
                    } : null
                });

                // Set product rows with 2 decimal places
                if (data.deliveryChallanItems && data.deliveryChallanItems.length > 0) {
                    const productRows = data.deliveryChallanItems.map(item => ({
                        description: item.product.description,
                        value: item.product.id,
                        label: `${item.product.description} - ${item.product.moc} - ${item.product.size}`,
                        productremarks: item.productremarks || '',
                        quantity: item.quantity,
                        uom: item.uom || '',
                        unitRateInr: parseFloat((item.unitrateinr || 0).toFixed(2)),
                        gstPercent: parseFloat((item.gst || 0).toFixed(2)),
                        amount: parseFloat((item.amount || 0).toFixed(2)),
                        totalinr: parseFloat((item.totalinr || 0).toFixed(2)),
                        hsnsacno: item.product.hsnsacno || '',
                        partno: item.partno || '',
                        serialnumber: item.serialnumber || '',
                        moc: item.product.moc || '',
                        type: item.product.type || '',
                        size: item.product.size || '',
                        rating: item.product.rating || '',
                        classes: item.product.classes || '',
                        schedule: item.product.schedule || '',
                        certification: item.product.certification || '',
                    }));
                    setRows(productRows);
                }

                // Set additional charges with 2 decimal places
                if (data.additionalCharges && data.additionalCharges.length > 0) {
                    const charges = data.additionalCharges.map(charge => ({
                        id: Date.now() + Math.random(),
                        description: charge.chargetype,
                        chargepercent: parseFloat((charge.chargepercent || 0).toFixed(2)),
                        amount: parseFloat((charge.chargeamount || 0).toFixed(2)),
                        gstPercent: parseFloat((charge.chargegst || 0).toFixed(2)),
                        isPercentBased: charge.isPercentBased !== false,
                    }));
                    setAdditionalCharges(charges);
                } else {
                    setAdditionalCharges([createEmptyCharge()]);
                }

            } catch (error) {
                console.error("Error fetching delivery challan:", error);
                Swal.fire("Error", "Failed to load delivery challan data", "error");
            } finally {
                setIsLoading(false);
            }
        };

        fetchDeliveryChallan();
    }, [id, navigate]);

    // Create empty charge row
    const createEmptyCharge = () => ({
        id: Date.now(),
        description: "",
        chargepercent: 0,
        amount: 0,
        gstPercent: 0,
        isPercentBased: true,
    });

    // Handle common fields data change
    const handleCommonFormData = (data) => {
        setCommonFieldsData(data);
    };

    // Fetch product data
    const getPumpProductData = async () => {
        try {
            const response = await axios.get(
                `${BASE_URL}product/page/ptype/0/10/desc/id/1/Finished,Raw Material, Semi Finished,Services/${productSearch}`,
                { headers: authHeader() }
            );
            const products = response.data.Index || response.data.data || [];
            setProductOptions(
                products.map((product) => ({
                    value: product.id,
                    label: `${product.description} - ${product.moc} - ${product.size} - ${product.tagnumber} - ${product.serialnumber} - ${product.partno}`,
                    description: product.description,
                    uom: product.unitofmeasurement || UNIT_OPTIONS[0].value,
                    igst: product.igst || 0,
                    partno: product.partno,
                    serialnumber: product.serialnumber,
                    modelnumber: product.modelnumber,
                    tagnumber: product.tagnumber,
                    hsnsacno: product.hsnsacno,
                    moc: product.moc || "",
                    type: product.type || "",
                    size: product.size || "",
                    rating: product.rating || "",
                    classes: product.classes || "",
                    schedule: product.schedule || "",
                    certification: product.certification || "",
                }))
            );
        } catch (error) {
            Swal.fire("Error", "Failed to fetch product data", "error");
        }
    };

    useEffect(() => {
        getPumpProductData();
    }, [productSearch]);

    const handleProductSearchChange = debounce((inputValue) => {
        setProductSearch(inputValue ? encodeURIComponent(inputValue) : encodeURIComponent(" "));
    }, 500);

    // Handle product selection
    const handleProductChange = (selectedOption) => {
        setSelectedProduct(selectedOption);
        const selectedProductData = productOptions.find(
            (product) => product.value === selectedOption.value
        );

        if (selectedProductData) {
            const newRow = {
                description: selectedProductData.description,
                value: selectedProductData.value,
                label: selectedProductData.label,
                productremarks: "",
                quantity: "",
                uom: selectedProductData.uom || "",
                unitRateInr: parseFloat((selectedProductData.unitrateinr || 0).toFixed(2)),
                gstPercent: parseFloat((selectedProductData.igst || 0).toFixed(2)),
                amount: parseFloat((selectedProductData.unitrateinr || 0).toFixed(2)),
                totalinr: parseFloat(((selectedProductData.unitrateinr || 0) * (1 + (selectedProductData.igst || 0) / 100)).toFixed(2)),
                hsnsacno: selectedProductData.hsnsacno,
                partno: selectedProductData.partno,
                serialnumber: selectedProductData.serialnumber,
                moc: selectedProductData.moc || "",
                type: selectedProductData.type || "",
                size: selectedProductData.size || "",
                rating: selectedProductData.rating || "",
                classes: selectedProductData.classes || "",
                schedule: selectedProductData.schedule || "",
                certification: selectedProductData.certification || "",
            };
            setRows([...rows, newRow]);
        }
    };

    // Handle charge changes with 2 decimal places
    const handleChargeChange = (index, field, value, isBlur = false) => {
        setAdditionalCharges(prev => {
            const newCharges = [...prev];
            const charge = { ...newCharges[index] };
            const netTotal = rows.reduce((sum, row) => sum + (row.totalinr || 0), 0);

            if (field === "chargepercent") {
                const percentage = Math.min(value, 100);
                charge.chargepercent = parseFloat(percentage.toFixed(2));

                if (isBlur || percentage === 100) {
                    const calculatedAmount = (percentage * netTotal) / 100;
                    charge.amount = parseFloat(calculatedAmount.toFixed(2));
                } else {
                    charge.amount = 0;
                }
            }
            else if (field === "amount") {
                charge.amount = parseFloat(value.toFixed(2));
                charge.chargepercent = 0;
            }
            else if (field === "gstPercent") {
                charge.gstPercent = parseFloat(value.toFixed(2));
            }
            else {
                charge[field] = value;
            }

            newCharges[index] = charge;
            return newCharges;
        });
    };

    // Add new charge row
    const addNewChargeRow = () => {
        setAdditionalCharges((prev) => [...prev, createEmptyCharge()]);
    };

    // Remove charge row
    const removeChargeRow = (index) => {
        if (additionalCharges.length > 1) {
            setAdditionalCharges((prev) => prev.filter((_, i) => i !== index));
        }
    };

    // Remove product row
    const removeRow = (index) => {
        const updatedRows = [...rows];
        updatedRows.splice(index, 1);
        setRows(updatedRows);
    };

    // Handle row changes with 2 decimal places
    const handleRowChange = (index, key, value) => {
        const updatedRows = [...rows];
        updatedRows[index][key] = value;

        if (["quantity", "unitRateInr", "gstPercent"].includes(key)) {
            const quantity = parseFloat(updatedRows[index].quantity) || 0;
            const unitRateInr = parseFloat(updatedRows[index].unitRateInr) || 0;
            const gstPercent = parseFloat(updatedRows[index].gstPercent) || 0;

            const amount = parseFloat((quantity * unitRateInr).toFixed(2));
            const gstAmount = parseFloat(((amount * gstPercent) / 100).toFixed(2));
            const totalinr = parseFloat((amount + gstAmount).toFixed(2));

            updatedRows[index].amount = amount;
            updatedRows[index].totalinr = totalinr;
        }

        setRows(updatedRows);
    };

    // Calculate products total with 2 decimal places
    const productsTotal = useMemo(() => {
        const total = rows.reduce((total, row) => {
            const rowTotal = (row.quantity || 0) * (row.unitRateInr || 0) * (1 + (row.gstPercent || 0) / 100);
            return total + rowTotal;
        }, 0);
        return parseFloat(total.toFixed(2));
    }, [rows]);

    useEffect(() => {
        // Recalculate charge amounts based on percentages when productsTotal changes
        setAdditionalCharges(prevCharges => {
            return prevCharges.map(charge => {
                if (charge.chargepercent > 0) {
                    return {
                        ...charge,
                        amount: parseFloat(((charge.chargepercent * productsTotal) / 100).toFixed(2))
                    };
                }
                return charge;
            });
        });
    }, [productsTotal]);

    // Calculate charges total with 2 decimal places
    const additionalChargesTotal = useMemo(() => {
        const total = additionalCharges.reduce((total, charge) => {
            const chargeTotal = (charge.amount || 0) * (1 + (charge.gstPercent || 0) / 100);
            return total + chargeTotal;
        }, 0);
        return parseFloat(total.toFixed(2));
    }, [additionalCharges]);

    // Calculate gross total and round to nearest integer with 2 decimal places
    const grossTotal = parseFloat((productsTotal + additionalChargesTotal).toFixed(2));
    const roundedGrossTotal = Math.round(grossTotal);
    const grandTotal = roundedGrossTotal;

    // Form validation
    const validateForm = () => {
        if (!isEnquiryValid) {
            Swal.fire("Error", "Please enter a valid sales invoice number", "error");
            return false;
        }
        if (!commonFieldsData.customer) {
            Swal.fire("Error", "Please select a customer", "error");
            return false;
        }
        if (!commonFieldsData.customerMultipleAddress) {
            Swal.fire("Error", "Please select customer address", "error");
            return false;
        }
        if (rows.length === 0) {
            Swal.fire("Error", "Please add at least one product", "error");
            return false;
        }
        return true;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsLoading(true);

        // Prepare sales items with 2 decimal places
        const salesItems = rows.map((row) => ({
            product: { id: row.value },
            quantity: row.quantity,
            uom: row.uom,
            unitrateinr: parseFloat(row.unitRateInr.toFixed(2)),
            gst: parseFloat(row.gstPercent.toFixed(2)),
            amount: parseFloat(row.amount.toFixed(2)),
            totalinr: parseFloat(row.totalinr.toFixed(2)),
            productremarks: row.productremarks,
            partno: row.partno || "",
            hsnsacno: row.hsnsacno || "",
            serialnumber: row.serialnumber || "",
            moc: row.moc || "",
            type: row.type || "",
            size: row.size || "",
            rating: row.rating || "",
            classes: row.classes || "",
            schedule: row.schedule || "",
            certification: row.certification || "",
        }));

        // Prepare additional charges with 2 decimal places
        const additionalChargesData = additionalCharges
            .filter((charge) => charge.description && (charge.amount > 0 || charge.chargepercent > 0))
            .map((charge) => {
                const chargeAmount = parseFloat(charge.amount.toFixed(2));
                const gstPercent = parseFloat(charge.gstPercent.toFixed(2));
                const gstAmount = parseFloat((chargeAmount * gstPercent / 100).toFixed(2));
                const chargetotal = parseFloat((chargeAmount + gstAmount).toFixed(2));

                return {
                    chargetype: charge.description,
                    chargepercent: parseFloat(charge.chargepercent.toFixed(2)),
                    chargeamount: chargeAmount,
                    chargegst: gstPercent,
                    chargetotal: chargetotal,
                    isPercentBased: charge.isPercentBased,
                };
            });

        // Calculate totals with 2 decimal places
        const additionalChargesTotalCalc = parseFloat(additionalChargesData.reduce(
            (sum, charge) => sum + charge.chargetotal,
            0
        ).toFixed(2));

        const productsTotalCalc = parseFloat(salesItems.reduce(
            (sum, item) => sum + item.totalinr,
            0
        ).toFixed(2));

        const grossTotalCalc = parseFloat((productsTotalCalc + additionalChargesTotalCalc).toFixed(2));

        // Prepare payload
        const payload = {
            customer: commonFieldsData.customer ? { id: commonFieldsData.customer.value } : null,
            contactperson: commonFieldsData.customerDetails?.contactperson,
            contactno: commonFieldsData.customerDetails?.contactno,
            email: commonFieldsData.customerDetails?.email,
            customeraddress: commonFieldsData.customerMultipleAddress
                ? { id: commonFieldsData.customerMultipleAddress.value }
                : null,
            freight: commonFieldsData.customerDetails.freight,
            remarks: commonFieldsData.remarks,
            packingandforwarding: commonFieldsData.customerDetails.packingandforwarding,
            paymentterm: commonFieldsData.customerDetails.paymentterm,
            shipto: commonFieldsData.shipTo ? { id: commonFieldsData.shipTo.value } : null,
            billto: commonFieldsData.billTo ? { id: commonFieldsData.billTo.value } : null,
            branch: commonFieldsData.branch ? { id: commonFieldsData.branch.value } : null,
            warehouse: commonFieldsData.warehouse ? { id: commonFieldsData.warehouse.value } : null,
            company: {
                id: "1"
            },
            salesItems: salesItems,
            additionalCharges: additionalChargesData.length > 0 ? additionalChargesData : null,
            nettotalproduct: productsTotalCalc,
            additionalchargestotal: additionalChargesTotalCalc,
            grosstotal: grossTotalCalc,
            grandtotalproduct: grossTotalCalc,
            salestype: "Manufacturing",
            salescategory: "Domestic",
            invoiceno,
            invoicedate,
            dcdate,
            dcno,
            policyno,
            tallyreferenceno,
            termsofdelivery,
            buyerorderno,
            buyerorderdate,
            destination,
            dispatchthrough,
            reference,
            validupto,
            status,
        };

        try {
            const response = await axios.post(`${BASE_URL}sales/dcToSales/${id}`, payload, {
                headers: authHeader(),
            });
            Swal.fire("Success", "Sales invoice converted successfully", "success");
            navigate("/salesindex/1");
        } catch (error) {
            console.error("Convert error:", error);
            Swal.fire(
                "Error",
                error.response?.data?.message ||
                error.response?.data ||
                "Failed to convert to sales invoice",
                "error"
            );
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    return (
        <div className="container">
            <Form onSubmit={handleSubmit}>
                {/* Invoice Header Information */}
                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Sales Invoice Number: *</Form.Label>
                            <Form.Control
                                type="text"
                                value={invoiceno}
                                onChange={e => {
                                    setinvoiceno(e.target.value);
                                    setFormSubmitted(false);
                                }}
                                isInvalid={
                                    (formSubmitted && !isEnquiryValid) ||
                                    enquiryValidationMsg.includes('exists') ||
                                    enquiryValidationMsg.includes('Error') ||
                                    enquiryValidationMsg.includes('allowed')
                                }
                                required
                            />
                            {enquiryValidationMsg && (
                                <Form.Control.Feedback type={
                                    enquiryValidationMsg.includes('exists') ||
                                        enquiryValidationMsg.includes('Error') ||
                                        enquiryValidationMsg.includes('allowed') ?
                                        "invalid" : "valid"
                                }>
                                    {enquiryValidationMsg}
                                </Form.Control.Feedback>
                            )}
                            {formSubmitted && !isEnquiryValid && !enquiryValidationMsg && (
                                <Form.Control.Feedback type="invalid">
                                    Please enter a valid sales invoice number
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Sales Invoice Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={invoicedate}
                                onChange={e => setinvoicedate(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="tallyreferenceno">
                            <Form.Label>Tally Reference No.</Form.Label>
                            <Form.Control
                                type="text"
                                value={tallyreferenceno}
                                onChange={(e) => settallyreferenceno(e.target.value)}
                            />
                        </Form.Group>
                    </Col>

                    {/* Reference Documents Section */}
                    <Col md={6}>
                        <Form.Group controlId="dcno">
                            <Form.Label>Delivery Challan No.</Form.Label>
                            <Form.Control
                                type="text"
                                value={dcno}
                                onChange={(e) => setdcno(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="dcdate">
                            <Form.Label>Delivery Challan Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={dcdate}
                                onChange={(e) => setdcdate(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="buyerorderno">
                            <Form.Label>Buyer Order No.</Form.Label>
                            <Form.Control
                                type="text"
                                value={buyerorderno}
                                onChange={(e) => setbuyerorderno(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="buyerorderdate">
                            <Form.Label>Buyer Order Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={buyerorderdate}
                                onChange={(e) => setAmendmentdate(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                {/* Common DC Fields */}
                <CommonDcFields
                    onDataChange={handleCommonFormData}
                    initialData={commonFieldsData}
                />

                {/* Dispatch & Delivery Information */}
                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group controlId="dispatchthrough">
                            <Form.Label>Dispatch Through</Form.Label>
                            <Form.Control
                                type="text"
                                value={dispatchthrough}
                                onChange={(e) => setdispatchthrough(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="destination">
                            <Form.Label>Destination</Form.Label>
                            <Form.Control
                                type="text"
                                value={destination}
                                onChange={(e) => setdestination(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="termsofdelivery">
                            <Form.Label>Delivery Term</Form.Label>
                            <Form.Control
                                type="text"
                                value={termsofdelivery}
                                onChange={(e) => settermsofdelivery(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="policyno">
                            <Form.Label>Policy No</Form.Label>
                            <Form.Control
                                type="text"
                                value={policyno}
                                onChange={(e) => setpolicyno(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="validupto">
                            <Form.Label>Valid Upto Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={validupto}
                                onChange={(e) => setvalidupto(e.target.value)}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={12}>
                        <Form.Group controlId="reference">
                            <Form.Label>Reference</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={reference}
                                onChange={(e) => setreference(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                {/* Products Section */}
                <div className="mt-4">
                    <h5>Products</h5>
                    <Row>
                        <Col md="12">
                            <Form.Group>
                                <Form.Label>Select Product</Form.Label>
                                <Select
                                    value={selectedProduct}
                                    onChange={handleProductChange}
                                    options={productOptions}
                                    placeholder="Search and select product"
                                    onInputChange={handleProductSearchChange}
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    }}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Common ProductTable Component */}
                    <ProductTableSales
                        rows={rows}
                        unitOptions={UNIT_OPTIONS}
                        onRowChange={handleRowChange}
                        onRemoveRow={removeRow}
                    />
                </div>

                {/* Charges Section */}
                <ChargesSection
                    additionalCharges={additionalCharges}
                    chargeOptions={CHARGE_OPTIONS}
                    productsTotal={productsTotal}
                    additionalChargesTotal={additionalChargesTotal}
                    grossTotal={grossTotal}
                    grandTotal={grandTotal}
                    onChargeChange={handleChargeChange}
                    onAddCharge={addNewChargeRow}
                    onRemoveCharge={removeChargeRow}
                />

                {/* Submit Buttons */}
                <div className="d-flex justify-content-end mt-4">
                    <button
                        type="submit"
                        className="btn btn-primary me-2"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" />
                                <span className="ms-2">Converting...</span>
                            </>
                        ) : (
                            "Convert to Sales Invoice"
                        )}
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => navigate(-1)}
                    >
                        Cancel
                    </button>
                </div>
            </Form>
        </div>
    );
}

export default DCMDConvert;